import React, { useState, useEffect } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { toast } from "sonner";
import WeatherDisplay from './WeatherDisplay';
import ForecastSelector from './ForecastSelector';
import StreamingLocationInput from './StreamingLocationInput';
import SafeComponent from './SafeComponent';
import { getLayerRecommendations } from '../lib/layerRecommendationsApi';
import { reverseGeocode } from '../lib/reverseGeocode';
import geocode from '../lib/geocode';
import { readTodayFromUrl, writeTodayToUrl } from '../lib/urlState';
import { publishMood } from '../lib/moodBus';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useSafeState } from '../hooks/useSafeState';
const Weather = ({
  theme,
  onRecommendationShown
}) => {
  // Read URL params once — used to hydrate initial state so the location input
  // shows the city name on first paint (not "Where are you?" placeholder).
  const urlStateInitial = React.useMemo(() => readTodayFromUrl(), []);
  const [location, setLocation] = useSafeState(urlStateInitial?.city || '');
  const [coordinates, setCoordinates] = useSafeState(null);
  const [inputError, setInputError] = useSafeState('');
  const [forecastHours, setForecastHours] = useSafeState(urlStateInitial?.forecastHours || 0);
  const {
    handleError
  } = useErrorHandler();
  
  // Track increment calls at this level to prevent multiple increments
  const lastRecommendationRef = React.useRef(null);

  // URL state hydration: ?city=X&when=now|3h|6h|8h|12h|yesterday.
  // city + forecastHours are seeded at initial state above; we only need to
  // resolve coordinates here. Skip the geolocation prompt entirely when URL
  // carries a shared forecast — makes every share clickable.
  const urlStateRef = React.useRef(urlStateInitial);
  useEffect(() => {
    const urlState = urlStateRef.current;
    if (!urlState) return;
    let cancelled = false;
    (async () => {
      try {
        const coords = await geocode(urlState.city);
        if (cancelled) return;
        setCoordinates(coords);
      } catch (err) {
        console.warn('Could not hydrate from URL:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (urlStateRef.current) return; // URL-hydrated; skip geolocation prompt.
    console.log('Weather component mounted, checking geolocation...');
    if ("geolocation" in navigator) {
      console.log('Geolocation is supported, requesting position...');
      navigator.geolocation.getCurrentPosition(async position => {
        console.log('Geolocation success:', position.coords);
        const coords = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        };
        setCoordinates(coords);

        // Show coords-as-fallback IMMEDIATELY so the user sees a location
        // label as soon as the forecast card paints, instead of an awkward
        // empty "Weather in" header that fills in 500-2000ms later when the
        // reverseGeocode call lands. Then swap to the real city name when
        // it resolves. Already done in error paths below; hoisted to the
        // happy path per /plan-eng-review of PLAN-speed.md.
        setLocation(`${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`);

        // Try to get actual location name
        try {
          const locationName = await reverseGeocode(coords.latitude, coords.longitude);
          console.log('Reverse geocoding result:', locationName);
          if (locationName) {
            setLocation(locationName);
            console.log('Location set to:', locationName);
          }
        } catch (error) {
          console.error('Failed to get location name:', error);
          // location already shows coords from above; nothing more to do.
        }
      }, error => {
        console.error("Geolocation error:", error.code, error.message);
        let errorMessage;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enter a location manually.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable. Please enter a location manually.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please enter a location manually.';
            break;
          default:
            errorMessage = 'Unable to retrieve your location. Please enter a location manually.';
            break;
        }
        setInputError(errorMessage);
      }, {
        timeout: 10000,
        enableHighAccuracy: false,
        maximumAge: 300000
      });
    } else {
      console.log('Geolocation not supported');
      setInputError('Geolocation is not supported by your browser. Please enter a location manually.');
    }
  }, []);
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['layerRecommendations', coordinates, forecastHours],
    queryFn: () => coordinates ? getLayerRecommendations({
      ...coordinates,
      forecastHours
    }) : null,
    enabled: !!coordinates,
    // 10-min staleTime matches the localStorage forecast TTL — without this,
    // a localStorage hit hydrates the query and React Query immediately
    // refetches, causing a "cached card flash → re-render" flicker. Emil + Eng
    // both flagged this in PLAN-speed.md review.
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    // Keep the previous recommendation visible when forecastHours toggles
    // (Now → 3h → 6h) so the user sees stale-but-relevant content during the
    // re-fetch instead of a blank loading state. Single biggest perceived-
    // speed win per Emil's review.
    placeholderData: keepPreviousData,
    retry: (failureCount, error) => {
      // Don't retry on rate limit errors
      if (error?.status === 429) return false;
      return failureCount < 3;
    },
    onError: error => {
      if (error?.status === 429) {
        const retryAfter = error.response?.headers?.get('Retry-After') || 30;
        toast.error(`Please wait ${retryAfter} seconds before trying again.`);
      } else {
        handleError(error, 'Weather Data');
      }
    }
  });
  const handleLocationSelect = selectedLocation => {
    setLocation(selectedLocation.name);
    setCoordinates({
      latitude: selectedLocation.latitude,
      longitude: selectedLocation.longitude
    });
    setInputError('');
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (coordinates) {
      refetch();
    } else {
      setInputError('Please select a location from the dropdown.');
    }
  };

  // Whenever we have a real city + forecast, mirror it into the URL so the
  // current tab is itself a shareable link. Skip while there's just a coord
  // fallback ("59.8586, 17.6389") — that's not shareable as text.
  useEffect(() => {
    if (!location) return;
    const looksLikeCoords = /^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(location);
    if (looksLikeCoords) return;
    writeTodayToUrl({ city: location, forecastHours });
  }, [location, forecastHours]);

  // Publish the current temperature for the mascot mood (sunflower / cactus / etc.).
  useEffect(() => {
    if (!data?.temperature) return;
    publishMood(data.temperature.display, {
      precipitation: data.conditions?.precipitation ?? 0
    });
  }, [data]);
  return <SafeComponent name="Weather">
      <div className="w-full max-w-md mx-auto rounded-2xl">
        <form onSubmit={handleSubmit} className="mb-4">
          <div className="space-y-2">
            <SafeComponent name="Location Input">
              <StreamingLocationInput
                onLocationSelect={handleLocationSelect}
                theme={theme}
                initialValue={location && !/^-?\d+\.\d+,\s*-?\d+\.\d+$/.test(location) ? location : ''}
              />
            </SafeComponent>
            {inputError && (
              <p role="alert" aria-live="polite" className="text-red-600 dark:text-red-400 text-xs">
                {inputError}
              </p>
            )}
            <div className="flex space-x-2">
              <Button
                type="submit"
                className="flex-grow bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 text-sm rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-foreground/30"
              >
                Find
              </Button>
              <SafeComponent name="Forecast Selector">
                <ForecastSelector forecastHours={forecastHours} setForecastHours={setForecastHours} />
              </SafeComponent>
            </div>
          </div>
        </form>
        
          <a
            href="https://wt.ls/layers.today"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center mt-3 mb-1 text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-gray-700 dark:hover:text-gray-300 underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground/60 transition-colors"
          >
            🌻 Sign up for personalized weather data
          </a>
        {isLoading && !data && (
          // Card-shaped skeleton matching the actual LayerRecommendation +
          // weather card geometry, so when data lands only the content
          // swaps in — the layout never reflows. Emil's perceived-speed
          // recommendation in PLAN-speed.md review.
          <div className="space-y-3 mt-3" aria-live="polite" aria-label="Checking the sky for you">
            <div className="flex items-center space-x-4 p-3 rounded-lg bg-foreground/5 animate-pulse">
              <span className="font-light text-5xl text-gray-400 dark:text-gray-600">?</span>
              <div className="flex-1 space-y-2">
                <div className="h-3 w-2/3 rounded-full bg-foreground/10" />
                <div className="h-3 w-1/3 rounded-full bg-foreground/10" />
              </div>
            </div>
            <div className="rounded-xl border border-foreground/10 bg-background/30 backdrop-blur-md p-3 space-y-3">
              <div className="h-5 w-1/2 rounded-full bg-foreground/10 animate-pulse" />
              <div className="h-9 w-2/3 rounded-md bg-foreground/10 animate-pulse" />
              <div className="flex gap-3">
                <div className="h-3 w-16 rounded-full bg-foreground/5 animate-pulse" />
                <div className="h-3 w-16 rounded-full bg-foreground/5 animate-pulse" />
              </div>
            </div>
          </div>
        )}
        {error && error.status !== 429 && (
          <div
            role="alert"
            aria-live="assertive"
            className="text-sm text-red-600 dark:text-red-400 space-y-2"
          >
            <p>{error.message || "Couldn't reach the forecast. Check your connection and try again."}</p>
            <Button
              type="button"
              onClick={() => refetch()}
              className="text-xs rounded-full bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-foreground/30"
            >
              Try again
            </Button>
          </div>
        )}
        {data && <SafeComponent name="Weather Display">
            <WeatherDisplay 
              data={data} 
              location={location} 
              coordinates={coordinates} 
              forecastHours={forecastHours} 
              onRecommendationShown={(recommendation) => {
                // Create a unique key for this recommendation
                const recommendationKey = `${coordinates?.latitude}-${coordinates?.longitude}-${forecastHours}-${recommendation?.layers}`;
                
                // Only increment if this is a new recommendation
                if (recommendationKey !== lastRecommendationRef.current) {
                  onRecommendationShown();
                  lastRecommendationRef.current = recommendationKey;
                }
              }} 
            />
          </SafeComponent>}
      </div>
    </SafeComponent>;
};
export default Weather;